import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Container, Row, Col, Card, Button, Form, Spinner, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';
import DatePicker from 'react-datepicker';
import { useNavigate } from 'react-router-dom';
import 'react-datepicker/dist/react-datepicker.css';
import budgetService from '../../../services/budgetService';
import { useAuth } from '../../../contexts/AuthContext';

// Importar ECharts
import BasicECharts from '../../common/BasicEChart';
import { LineChart, BarChart, PieChart } from 'echarts/charts';
import {
  GridComponent,
  TooltipComponent,
  LegendComponent,
  TitleComponent,
} from 'echarts/components';
import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';

// Importar componentes de Falcon
import Flex from '../../common/Flex';
import IconItem from '../../common/icon/IconItem';
import ObjectivesSummaryWidget from '../objectives/ObjectivesSummaryWidget';

echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  LegendComponent,
  LineChart,
  BarChart,
  PieChart,
  CanvasRenderer
]);

// Componente KPI Card
const KPICard = ({ title, value, change, icon, color, suffix = '', prefix = '' }) => {
  const isPositive = change > 0;
  const isNeutral = change === 0;
  
  return (
    <Card className="h-100 shadow-sm">
      <Card.Body>
        <Flex justifyContent="between" alignItems="start">
          <div>
            <p className="text-500 mb-1 fs-11 text-uppercase">{title}</p>
            <h3 className="mb-0 text-700">
              {prefix}{typeof value === 'number' ? value.toLocaleString() : value}{suffix}
            </h3>
            {change !== undefined && (
              <div className="mt-2">
                <Badge bg={isNeutral ? 'secondary' : isPositive ? 'success' : 'danger'} className="fs-11">
                  <FontAwesomeIcon 
                    icon={isNeutral ? 'minus' : isPositive ? 'arrow-up' : 'arrow-down'} 
                    className="me-1" 
                  />
                  {Math.abs(change).toFixed(1)}%
                </Badge>
                <span className="text-500 fs-11 ms-2">vs período anterior</span>
              </div>
            )}
          </div>
          <IconItem
            icon={icon}
            bg={`${color}-subtle`}
            color={color}
            size="lg"
            className="shadow-none"
          />
        </Flex>
      </Card.Body>
    </Card>
  );
};

// Gráfico de tendencia
const TrendChart = ({ data, title }) => {
  const options = useMemo(() => ({
    tooltip: {
      trigger: 'axis',
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      borderColor: '#e0e0e0',
      borderWidth: 1,
      textStyle: { color: '#333' },
      formatter: (params) => {
        let result = `<strong>${params[0].axisValue}</strong><br/>`;
        params.forEach(param => {
          const value = param.seriesName === 'Gasto' 
            ? `$${param.value.toLocaleString()}` 
            : param.value.toLocaleString();
          result += `${param.marker} ${param.seriesName}: ${value}<br/>`;
        });
        return result;
      }
    },
    legend: {
      data: ['Impresiones', 'Clics', 'Gasto'],
      bottom: 0,
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '15%',
      top: '10%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: data.map(d => {
        const date = new Date(d.date);
        return `${date.getDate()}/${date.getMonth() + 1}`;
      }),
      axisLine: { lineStyle: { color: '#e0e0e0' } },
      axisLabel: { color: '#666' }
    },
    yAxis: [
      {
        type: 'value',
        name: 'Impresiones/Clics',
        position: 'left',
        axisLine: { show: false },
        splitLine: { lineStyle: { color: '#f0f0f0' } },
        axisLabel: { 
          color: '#666',
          formatter: (val) => val >= 1000000 ? `${(val/1000000).toFixed(1)}M` : val >= 1000 ? `${(val/1000).toFixed(0)}K` : val
        }
      },
      {
        type: 'value',
        name: 'Gasto ($)',
        position: 'right',
        axisLine: { show: false },
        splitLine: { show: false },
        axisLabel: { 
          color: '#666',
          formatter: (val) => `$${val >= 1000 ? `${(val/1000).toFixed(0)}K` : val}`
        }
      }
    ],
    series: [
      {
        name: 'Impresiones',
        type: 'line',
        data: data.map(d => d.impressions),
        smooth: true,
        symbol: 'circle',
        symbolSize: 6,
        lineStyle: { width: 3, color: '#2c7be5' },
        itemStyle: { color: '#2c7be5' },
        areaStyle: {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: 'rgba(44, 123, 229, 0.3)' },
            { offset: 1, color: 'rgba(44, 123, 229, 0.05)' }
          ])
        }
      },
      {
        name: 'Clics',
        type: 'line',
        data: data.map(d => d.clicks),
        smooth: true,
        symbol: 'circle',
        symbolSize: 6,
        lineStyle: { width: 3, color: '#00d97e' },
        itemStyle: { color: '#00d97e' }
      },
      {
        name: 'Gasto',
        type: 'bar',
        yAxisIndex: 1,
        data: data.map(d => d.cost),
        barWidth: '40%',
        itemStyle: { 
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: '#f5803e' },
            { offset: 1, color: '#e63757' }
          ]),
          borderRadius: [4, 4, 0, 0]
        }
      }
    ]
  }), [data]);

  return (
    <Card className="h-100 shadow-sm">
      <Card.Header className="bg-light border-bottom">
        <h6 className="mb-0">
          <FontAwesomeIcon icon="chart-line" className="me-2 text-primary" />
          {title}
        </h6>
      </Card.Header>
      <Card.Body>
        {data.length > 0 ? (
          <BasicECharts
            echarts={echarts}
            options={options}
            style={{ height: '300px' }}
          />
        ) : (
          <div className="text-center py-5 text-muted">
            <FontAwesomeIcon icon="chart-line" size="3x" className="mb-3 text-300" />
            <p>No hay datos para el período seleccionado</p>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

// Gráfico de distribución por cliente
const ClientDistributionChart = ({ data }) => {
  const options = useMemo(() => ({
    tooltip: {
      trigger: 'item',
      formatter: (params) => {
        return `<strong>${params.name}</strong><br/>
          Gasto: $${params.value.toLocaleString()}<br/>
          Porcentaje: ${params.percent.toFixed(1)}%`;
      }
    },
    legend: {
      type: 'scroll',
      orient: 'vertical',
      right: 10,
      top: 20,
      bottom: 20,
    },
    series: [
      {
        name: 'Gasto por Cliente',
        type: 'pie',
        radius: ['40%', '70%'],
        center: ['35%', '50%'],
        avoidLabelOverlap: false,
        itemStyle: {
          borderRadius: 8,
          borderColor: '#fff',
          borderWidth: 2
        },
        label: {
          show: false,
        },
        emphasis: {
          label: {
            show: true,
            fontSize: 14,
            fontWeight: 'bold'
          }
        },
        labelLine: {
          show: false
        },
        data: data.slice(0, 10).map((client, index) => ({
          value: client.cost,
          name: client.clientName,
          itemStyle: {
            color: [
              '#2c7be5', '#00d97e', '#f5803e', '#e63757', '#27bcfd',
              '#6b5eae', '#fd7e14', '#00c9a7', '#5e72e4', '#f56036'
            ][index % 10]
          }
        }))
      }
    ]
  }), [data]);

  return (
    <Card className="h-100 shadow-sm">
      <Card.Header className="bg-light border-bottom">
        <h6 className="mb-0">
          <FontAwesomeIcon icon="chart-pie" className="me-2 text-info" />
          Distribución de Gasto por Cliente
        </h6>
      </Card.Header>
      <Card.Body>
        {data.length > 0 ? (
          <BasicECharts
            echarts={echarts}
            options={options}
            style={{ height: '300px' }}
          />
        ) : (
          <div className="text-center py-5 text-muted">
            <FontAwesomeIcon icon="chart-pie" size="3x" className="mb-3 text-300" />
            <p>No hay datos de clientes</p>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

// Tabla de rendimiento por cliente
const ClientPerformanceTable = ({ data }) => {
  const navigate = useNavigate();

  const handleRowClick = (clientId) => {
    navigate(`/campaign-dashboard?clientId=${clientId}`);
  };

  return (
    <Card className="shadow-sm">
      <Card.Header className="bg-light border-bottom">
        <h6 className="mb-0">
          <FontAwesomeIcon icon="table" className="me-2 text-success" />
          Rendimiento por Cliente
          <small className="text-muted ms-2">(clic en fila para ver detalle)</small>
        </h6>
      </Card.Header>
      <Card.Body className="p-0">
        <div className="table-responsive">
          <table className="table table-sm table-hover mb-0">
            <thead className="bg-light">
              <tr>
                <th>Cliente</th>
                <th className="text-end">Impresiones</th>
                <th className="text-end">Clics</th>
                <th className="text-end">CTR</th>
                <th className="text-end">Gasto</th>
                <th className="text-end">% Total</th>
              </tr>
            </thead>
            <tbody>
              {data.slice(0, 10).map((client, index) => {
                const ctr = client.impressions > 0 
                  ? ((client.clicks / client.impressions) * 100).toFixed(2) 
                  : '0.00';
                return (
                  <tr 
                    key={index} 
                    onClick={() => handleRowClick(client.clientId)}
                    style={{ cursor: 'pointer' }}
                    className="hover-highlight"
                  >
                    <td>
                      <div className="d-flex align-items-center">
                        <div 
                          className="rounded-circle me-2" 
                          style={{ 
                            width: 8, 
                            height: 8, 
                            backgroundColor: ['#2c7be5', '#00d97e', '#f5803e', '#e63757', '#27bcfd'][index % 5]
                          }} 
                        />
                        <span className="fw-medium">{client.clientName}</span>
                        <FontAwesomeIcon icon="external-link-alt" className="ms-2 text-muted" size="xs" />
                      </div>
                    </td>
                    <td className="text-end">{client.impressions.toLocaleString()}</td>
                    <td className="text-end">{client.clicks.toLocaleString()}</td>
                    <td className="text-end">
                      <Badge bg={parseFloat(ctr) > 1 ? 'success' : 'warning'}>{ctr}%</Badge>
                    </td>
                    <td className="text-end fw-semibold">${client.cost.toLocaleString()}</td>
                    <td className="text-end">
                      <Badge bg="primary">{client.percentage.toFixed(1)}%</Badge>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card.Body>
    </Card>
  );
};

const GeneralDashboard = () => {
  const { user } = useAuth();
  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [metrics, setMetrics] = useState(null);
  const [dateFrom, setDateFrom] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date;
  });
  const [dateTo, setDateTo] = useState(new Date());
  const [processingTime, setProcessingTime] = useState(0);

  const loadMetrics = useCallback(async (isInitial = false) => {
    if (isInitial) {
      setInitialLoading(true);
    } else {
      setRefreshing(true);
    }
    
    try {
      const result = await budgetService.getGeneralDashboardMetrics({
        dateFrom: dateFrom.toISOString().split('T')[0],
        dateTo: dateTo.toISOString().split('T')[0],
      });

      if (result.success) {
        setMetrics(result.data);
        setProcessingTime(result.data?.meta?.processingTimeMs || 0);
      } else {
        toast.error(result.error || 'Error al cargar métricas');
      }
    } catch (error) {
      console.error('Error loading metrics:', error);
      toast.error('Error al cargar las métricas del dashboard');
    } finally {
      setInitialLoading(false);
      setRefreshing(false);
    }
  }, [dateFrom, dateTo]);

  // Carga inicial
  useEffect(() => {
    loadMetrics(true);
  }, []); // Solo carga inicial una vez

  // Recargar cuando cambian las fechas (pero no en carga inicial)
  useEffect(() => {
    if (!initialLoading && metrics !== null) {
      loadMetrics(false);
    }
  }, [dateFrom, dateTo]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDatePreset = (days) => {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - days);
    setDateFrom(from);
    setDateTo(to);
  };

  if (initialLoading) {
    return (
      <Container fluid className="px-3">
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3 text-muted">Cargando dashboard ejecutivo...</p>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="px-3">
      {/* Header */}
      {/* Indicador de actualización sutil */}
      {refreshing && (
        <div 
          className="position-fixed top-0 start-0 end-0 bg-primary" 
          style={{ height: '3px', zIndex: 1050 }}
        >
          <div 
            className="bg-white h-100" 
            style={{ 
              width: '30%', 
              animation: 'loading-bar 1.5s ease-in-out infinite',
            }} 
          />
        </div>
      )}
      <style>
        {`
          @keyframes loading-bar {
            0% { margin-left: 0; width: 30%; }
            50% { margin-left: 35%; width: 50%; }
            100% { margin-left: 100%; width: 30%; }
          }
        `}
      </style>

      <div className="mb-4">
        <Row className="align-items-center">
          <Col>
            <h2 className="mb-2">
              <FontAwesomeIcon icon="tachometer-alt" className="me-2 text-primary" />
              Dashboard Ejecutivo
              {refreshing && (
                <Spinner 
                  animation="border" 
                  size="sm" 
                  variant="primary" 
                  className="ms-2" 
                />
              )}
            </h2>
            <h5 className="text-700 fw-semi-bold">
              Métricas de Performance y Branding - Meta Ads
              {processingTime > 0 && (
                <Badge bg="secondary" className="ms-2 fs-11 fw-normal">
                  {processingTime}ms
                </Badge>
              )}
            </h5>
          </Col>
          <Col xs="auto">
            <Button 
              variant="falcon-primary" 
              size="sm" 
              onClick={() => loadMetrics(false)}
              disabled={refreshing}
            >
              <FontAwesomeIcon icon="sync" className={refreshing ? 'fa-spin me-1' : 'me-1'} />
              Actualizar
            </Button>
          </Col>
        </Row>
      </div>

      {/* Filtros de fecha */}
      <Card className="mb-4 shadow-sm">
        <Card.Body>
          <Row className="align-items-end g-3">
            <Col md={3}>
              <Form.Label className="fw-semibold">Fecha Desde</Form.Label>
              <DatePicker
                selected={dateFrom}
                onChange={setDateFrom}
                dateFormat="dd/MM/yyyy"
                className="form-control"
                maxDate={dateTo}
              />
            </Col>
            <Col md={3}>
              <Form.Label className="fw-semibold">Fecha Hasta</Form.Label>
              <DatePicker
                selected={dateTo}
                onChange={setDateTo}
                dateFormat="dd/MM/yyyy"
                className="form-control"
                minDate={dateFrom}
                maxDate={new Date()}
              />
            </Col>
            <Col md={6}>
              <div className="d-flex gap-2 flex-wrap">
                <Button variant="outline-primary" size="sm" onClick={() => handleDatePreset(7)}>
                  Últimos 7 días
                </Button>
                <Button variant="outline-primary" size="sm" onClick={() => handleDatePreset(14)}>
                  Últimos 14 días
                </Button>
                <Button variant="outline-primary" size="sm" onClick={() => handleDatePreset(30)}>
                  Últimos 30 días
                </Button>
                <Button variant="outline-primary" size="sm" onClick={() => handleDatePreset(90)}>
                  Últimos 90 días
                </Button>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* KPIs principales */}
      <Row className="g-3 mb-4">
        <Col lg={3} md={6}>
          <KPICard
            title="Impresiones"
            value={metrics?.kpis?.impressions || 0}
            change={metrics?.changes?.impressions}
            icon="eye"
            color="primary"
          />
        </Col>
        <Col lg={3} md={6}>
          <KPICard
            title="Clics"
            value={metrics?.kpis?.clicks || 0}
            change={metrics?.changes?.clicks}
            icon="mouse-pointer"
            color="success"
          />
        </Col>
        <Col lg={3} md={6}>
          <KPICard
            title="Alcance"
            value={metrics?.kpis?.reach || 0}
            change={metrics?.changes?.reach}
            icon="users"
            color="info"
          />
        </Col>
        <Col lg={3} md={6}>
          <KPICard
            title="Gasto Total"
            value={metrics?.kpis?.cost || 0}
            change={metrics?.changes?.cost}
            icon="dollar-sign"
            color="warning"
            prefix="$"
          />
        </Col>
      </Row>

      {/* KPIs de eficiencia */}
      <Row className="g-3 mb-4">
        <Col lg={3} md={6}>
          <KPICard
            title="CTR"
            value={metrics?.kpis?.ctr || 0}
            change={metrics?.changes?.ctr}
            icon="percentage"
            color="primary"
            suffix="%"
          />
        </Col>
        <Col lg={3} md={6}>
          <KPICard
            title="CPM"
            value={metrics?.kpis?.cpm || 0}
            icon="chart-bar"
            color="success"
            prefix="$"
          />
        </Col>
        <Col lg={3} md={6}>
          <KPICard
            title="CPC"
            value={metrics?.kpis?.cpc || 0}
            icon="mouse-pointer"
            color="info"
            prefix="$"
          />
        </Col>
        <Col lg={3} md={6}>
          <KPICard
            title="Frecuencia"
            value={metrics?.kpis?.frequency || 0}
            icon="redo"
            color="warning"
          />
        </Col>
      </Row>

      {/* Resumen del período */}
      <Row className="g-3 mb-4">
        <Col md={4}>
          <Card className="bg-primary text-white h-100">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h4 className="text-white mb-0">{metrics?.summary?.totalClients || 0}</h4>
                  <small>Clientes Activos</small>
                </div>
                <FontAwesomeIcon icon="building" size="2x" className="opacity-50" />
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="bg-success text-white h-100">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h4 className="text-white mb-0">{metrics?.summary?.activeAccounts || 0}</h4>
                  <small>Cuentas con Actividad</small>
                </div>
                <FontAwesomeIcon icon="ad" size="2x" className="opacity-50" />
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="bg-info text-white h-100">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h4 className="text-white mb-0">{metrics?.summary?.daysInPeriod || 0}</h4>
                  <small>Días en Período</small>
                </div>
                <FontAwesomeIcon icon="calendar" size="2x" className="opacity-50" />
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Gráficos */}
      <Row className="g-3 mb-4">
        <Col lg={8}>
          <TrendChart 
            data={metrics?.dailyTrend || []} 
            title="Tendencia Diaria" 
          />
        </Col>
        <Col lg={4}>
          <ClientDistributionChart 
            data={metrics?.clientDistribution || []} 
          />
        </Col>
      </Row>

      {/* Progreso de Objetivos */}
      <Row className="g-3 mb-4">
        <Col>
          <ObjectivesSummaryWidget
            dateFrom={dateFrom.toISOString().split('T')[0]}
            dateTo={dateTo.toISOString().split('T')[0]}
            showTrend={true}
            maxCards={6}
          />
        </Col>
      </Row>

      {/* Tabla de rendimiento */}
      <Row>
        <Col>
          <ClientPerformanceTable 
            data={metrics?.clientDistribution || []} 
          />
        </Col>
      </Row>
    </Container>
  );
};

export default GeneralDashboard;
